import {
  BellIcon,
  CheckIcon,
  ExclamationCircleIcon,
  XIcon,
} from "@heroicons/react/outline";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { Box } from "~/design-system/Box";
import { Heading } from "~/design-system/Heading";
import { useBreakpoint } from "~/design-system/hooks";
import { Icon } from "~/design-system/Icon";
import { Inline } from "~/design-system/Inline";
import { Stack } from "~/design-system/Stack";
import { Text } from "~/design-system/Text";
import { Toggle } from "~/design-system/Toggle";
import { EyeIcon } from "~/screens/App/components/EyeIcon/EyeIcon";
import type { Filters } from "../../../../../../UserNotificationsScreen";

type TypesFilterProps = {
  setFilters: React.Dispatch<React.SetStateAction<Filters>>;
  filters: Filters;
};

const TypesFilter = (props: TypesFilterProps) => {
  const { t } = useTranslation();
  const { breakpoint } = useBreakpoint();
  const readLabel = t(
    "screens.UserNotificationsScreen.NotificationsFilter.readNotifications",
    "Read notifications"
  );

  const strongNotificationsLabel = t(
    "screens.UserNotificationsScreen.NotificationsFilter.strongNotifications",
    "Strong notifications"
  );

  const lightNotificationsLabel = t(
    "screens.UserNotificationsScreen.NotificationsFilter.lightNotifications",
    "Light notifications"
  );

  return (
    <Stack space="medium">
      <Heading level="5" css={{ color: "$gray700" }}>
        {t(
          "screens.UserNotificationsScreen.NotificationsFilter.types",
          "Types"
        )}
      </Heading>
      <Inline space="gutter">
        <Toggle
          on={props.filters.types.read}
          onChange={() => {
            const { types, ...othersFilters } = props.filters;
            const { read, ...otherTypes } = types;
            props.setFilters({
              types: { read: !read, ...otherTypes },
              ...othersFilters,
            });
          }}
          onIcon={<CheckIcon />}
          offIcon={<XIcon />}
          align="left"
          width={breakpoint === "tablet" ? "auto" : "full"}
          justify="inherit"
          label={
            <Box display="flex" alignItems="center" gap="xxsmall">
              <Icon
                label={readLabel}
                size="small"
                css={{ overflow: "visible" }}
              >
                <EyeIcon />
              </Icon>
              <Text size="standard">{readLabel}</Text>
            </Box>
          }
        />
        <Toggle
          on={props.filters.types.strong}
          onChange={() => {
            const { types, ...othersFilters } = props.filters;
            const { strong, ...otherTypes } = types;
            props.setFilters({
              types: { strong: !strong, ...otherTypes },
              ...othersFilters,
            });
          }}
          onIcon={<CheckIcon />}
          offIcon={<XIcon />}
          align="left"
          width={breakpoint === "tablet" ? "auto" : "full"}
          justify="inherit"
          label={
            <Box display="flex" alignItems="center" gap="xxsmall">
              <Icon
                label={strongNotificationsLabel}
                size="small"
                css={{ overflow: "visible" }}
              >
                <ExclamationCircleIcon />
              </Icon>
              <Text size="standard">{strongNotificationsLabel}</Text>
            </Box>
          }
        />
        <Toggle
          on={props.filters.types.weak}
          onChange={() => {
            const { types, ...othersFilters } = props.filters;
            const { weak, ...otherTypes } = types;
            props.setFilters({
              types: { weak: !weak, ...otherTypes },
              ...othersFilters,
            });
          }}
          onIcon={<CheckIcon />}
          offIcon={<XIcon />}
          align="left"
          width={breakpoint === "tablet" ? "auto" : "full"}
          justify="inherit"
          label={
            <Box display="flex" alignItems="center" gap="xxsmall">
              <Icon
                label={lightNotificationsLabel}
                size="small"
                css={{ overflow: "visible" }}
              >
                <BellIcon />
              </Icon>
              <Text size="standard">{lightNotificationsLabel}</Text>
            </Box>
          }
        />
      </Inline>
    </Stack>
  );
};

export { TypesFilter };
